/* eslint-disable fp/no-mutation */
/* eslint-disable fp/no-mutating-methods */
import React, { ReactElement, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { compose, tap } from 'ramda'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import Snackbar from '@mui/material/Snackbar'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import {
  BackIcon,
  CancelIcon,
  EditIcon,
  GiveCreditIcon,
  SubmitIcon,
  ViewIcon,
} from 'src/v3/core/icons'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers'

import axios, { pluckData } from 'src/utilities/http-client'
import theme from 'src/theme'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import CourseList from 'src/v3/manage/components/course-list'
import UserList from 'src/v3/manage/components/user-list'
import Loading from 'src/v3/components/loading'
import {
  selectAssignmentById,
  selectUsersByAssignmentId,
  selectActiveLinkedService,
  selectUsersStatus,
  fetchUsers,
  selectUsersByLinkedService,
  fetchGroups,
  selectGroupsWithDefaults,
  selectGroupsStatus,
  fetchCourses,
  selectCourses,
  selectCoursesByAssignmentByID,
  selectCoursesStatus,
  fetchAssignments,
  selectAssignmentsStatus,
  fetchTrainingSeries,
  selectTrainingSeriesByLinkedService,
  selectTrainingSeriesStatus,
  fetchCompletedCourses,
  selectCompletedCoursesStatus,
  selectCompletedCoursesByAssignment,
  selectLicensesStatus,
  fetchLicenses,
  selectCompletedAssignmentsWithinThreshold,
  selectCompletedAssignmentsWithinThresholdStatus,
  fetchCompletedAssignmentsWithinThreshold,
  fetchCompletedAssignments,
} from '../manage.slice'

dayjs.extend(utc)

let updateAssignment = (
  id: string,
  linkedServiceID: string,
  name: string,
  dueDate: any,
  userIDs: string[],
  courseIDs: string[],
  groupIDs: string[] = [],
) =>
  axios
    .put<unknown>('/api/v2/manage/assignment', {
      id,
      linkedServiceID,
      name,
      courseIDs,
      userIDs,
      dueDate,
      groupIDs,
    })
    .then(pluckData)

export default (): ReactElement => {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()
  let { assignmentId } = useParams()

  let assignment = useAppSelector(selectAssignmentById(assignmentId!))
  let assignmentUsers = useAppSelector(selectUsersByAssignmentId(assignmentId!))
  let assignmentCourses = useAppSelector(
    selectCoursesByAssignmentByID(assignmentId!),
  )
  let assignmentsStatus = useAppSelector(selectAssignmentsStatus)
  let assignmentsCompletedWithinThreshold = useAppSelector(
    selectCompletedAssignmentsWithinThreshold(assignmentId || ''),
  )
  let assignmentsCompletedWithinThresholdStatus = useAppSelector(
    selectCompletedAssignmentsWithinThresholdStatus(assignmentId || ''),
  )

  let activeLinkedService = useAppSelector(selectActiveLinkedService)

  let users = useAppSelector(selectUsersByLinkedService(activeLinkedService))
  let usersStatus = useAppSelector(selectUsersStatus)
  let groups = useAppSelector(selectGroupsWithDefaults(activeLinkedService))
  let groupsStatus = useAppSelector(selectGroupsStatus)

  let trainingSeries = useAppSelector(
    selectTrainingSeriesByLinkedService(activeLinkedService),
  )
  let trainingSeriesStatus = useAppSelector(selectTrainingSeriesStatus)
  let completedCourses = useAppSelector(
    selectCompletedCoursesByAssignment(assignmentId || ''),
  )
  let completedCoursesStatus = useAppSelector(selectCompletedCoursesStatus)

  let courses = useAppSelector(selectCourses)
  let coursesStatus = useAppSelector(selectCoursesStatus)

  let licensesStatus = useAppSelector(selectLicensesStatus)

  let [assignmentName, setAssignmentName] = useState<string>(
    assignment?.name || '',
  )
  let [checkedUsers, setCheckedUsers] = useState<string[]>([])
  let [checkedCourses, setCheckedCourses] = useState<string[]>([])
  let [dueDate, setDueDate] = useState<any>(assignment?.dueDate)
  let [editing, setEditing] = useState<string>('')
  let [dialogOpen, setDialogOpen] = useState<boolean>(false)
  let [saving, setSaving] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')

  let hydratedCourses =
    editing === 'editing'
      ? courses
      : assignmentCourses.map((c) => ({
          ...c,
          assignmentData: assignmentUsers.reduce(
            (data, u) => {
              let completedCourseWithUser = completedCourses.find(
                (cc) => cc.courseID === c.id && cc.userID === u.id,
              )
              if (completedCourseWithUser) {
                data.completedUsers.push(u.name)
                // eslint-disable-next-line no-param-reassign
                data.completedDates[u.name] = dayjs(
                  completedCourseWithUser.dateCompleted,
                ).format('MM/DD/YYYY')
              } else {
                data.pendingUsers.push(u.name)
              }
              return data
            },
            {
              completedUsers: [],
              pendingUsers: [],
              completedDates: {},
            } as NonNullable<{
              completedUsers: string[]
              pendingUsers: string[]
              completedDates: Record<string, string>
            }>,
          ),
        }))

  let userCourseData: Record<
    string,
    { pending: string[]; completed: string[] }
  > = {}
  assignmentUsers.forEach((u) => {
    let userData: (typeof userCourseData)[string] = {
      pending: [],
      completed: [],
    }
    assignmentCourses.forEach((c) => {
      if (
        completedCourses.some(
          (cc) => cc.courseID === c.id && cc.userID === u.id,
        )
      ) {
        userData.completed.push(c.name)
      } else {
        userData.pending.push(c.name)
      }
    })
    userCourseData[u.id] = userData
  })

  let ref = useRef()

  useEffect(() => {
    if (editing === 'approving') {
      ;(ref.current as any)?.scrollIntoView({
        behavior: 'smooth',
      })
    }
  }, [editing])

  useEffect(() => {
    if (usersStatus === 'uninitialized') {
      dispatch(fetchUsers())
    }
  }, [usersStatus])

  useEffect(() => {
    if (groupsStatus === 'uninitialized') {
      dispatch(fetchGroups())
    }
  }, [groupsStatus])

  useEffect(() => {
    if (assignmentsStatus === 'uninitialized') {
      dispatch(fetchAssignments())
    }
  }, [assignmentsStatus])

  useEffect(() => {
    if (trainingSeriesStatus === 'uninitialized') {
      dispatch(fetchTrainingSeries())
    }
  }, [trainingSeriesStatus])

  useEffect(() => {
    if (completedCoursesStatus === 'uninitialized') {
      dispatch(fetchCompletedCourses())
    }
  }, [completedCoursesStatus])

  useEffect(() => {
    if (coursesStatus === 'uninitialized') {
      dispatch(fetchCourses())
    }
  }, [coursesStatus])

  useEffect(() => {
    if (
      assignmentId &&
      (!assignmentsCompletedWithinThresholdStatus ||
        assignmentsCompletedWithinThresholdStatus === 'uninitialized')
    ) {
      dispatch(fetchCompletedAssignmentsWithinThreshold(assignmentId))
    } else if (assignmentsCompletedWithinThresholdStatus === 'fulfilled') {
      if (
        assignmentsCompletedWithinThreshold.length > 0 &&
        Math.abs(
          dayjs(assignment?.updatedTime)
            .utc(true)
            .local()
            .diff(dayjs(), 'second'),
        ) < 30
      ) {
        setDialogOpen(true)
      }
    }
  }, [assignmentsCompletedWithinThresholdStatus, assignmentId])

  useEffect(() => {
    if (
      activeLinkedService !== 'unselected' &&
      (!licensesStatus[activeLinkedService] ||
        licensesStatus[activeLinkedService] === 'uninitialized')
    ) {
      dispatch(fetchLicenses(activeLinkedService))
    }
  }, [licensesStatus, activeLinkedService])

  if (activeLinkedService !== assignment?.linkedServiceID) {
    navigate('/manage/assignments')
  }

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<BackIcon />}
        sx={{ position: 'absolute' }}
        onClick={() => navigate('/manage/assignments')}
      >
        Back
      </Button>
      <Box>
        <div className="flex items-center justify-center my-2">
          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
            {editing ? <EditIcon /> : <ViewIcon />}
          </Avatar>
          <Typography
            className="pl-2"
            variant="h4"
            style={{
              fontFamily: 'smileAndWave',
            }}
          >
            {assignment?.name || 'Unnamed Assignment'}
          </Typography>
          {!editing ? (
            <Tooltip title="Edit" placement="right">
              <IconButton
                color="primary"
                className="ml-2"
                onClick={() => {
                  let current = editing
                  setEditing('editing')
                  if (current !== 'editing') {
                    setCheckedUsers(assignment?.users || [])
                  }
                  if (current !== 'editing') {
                    setCheckedCourses(assignment?.courses || [])
                  }
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="View" placement="right">
              <IconButton
                color="primary"
                className="ml-2"
                onClick={() => {
                  setAssignmentName(assignment?.name || '')
                  setCheckedUsers(assignment?.users || [])
                  setCheckedCourses(assignment?.courses || [])
                  setDueDate(assignment?.dueDate)
                  setEditing('')
                }}
              >
                <ViewIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>
        {editing !== 'editing' && (
          <>
            <Typography textAlign="center" color="text.secondary">
              Due {dayjs(assignment?.dueDate).format('MMMM DD, YYYY')}
            </Typography>
          </>
        )}
        <Divider className="my-3" />
        {editing === 'editing' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography variant="h5" textAlign="center">
              Assignment Name
            </Typography>
            <TextField
              sx={{ width: 300 }}
              variant="outlined"
              className="mt-2 mb-3"
              label="Name"
              name="name"
              type="text"
              value={assignmentName}
              onChange={(event: React.ChangeEvent) => {
                setAssignmentName((event.target as HTMLInputElement).value)
              }}
              disabled={!editing}
            />
            <Typography variant="h5" textAlign="center">
              Due Date
            </Typography>
            <div className="mt-2 mb-3">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  sx={{ width: 300 }}
                  label="Complete By Date"
                  value={dayjs(dueDate)}
                  onChange={setDueDate}
                  disabled={!editing}
                />
              </LocalizationProvider>
            </div>
          </Box>
        )}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            columnGap: 2,
          }}
        >
          <Box>
            <Typography variant="h5" textAlign="center">
              Courses
            </Typography>
            <CourseList
              courses={hydratedCourses}
              trainingSeries={trainingSeries}
              checkbox={
                editing === 'editing'
                  ? {
                      onClick: setCheckedCourses,
                      checked: checkedCourses,
                    }
                  : undefined
              }
              stats={editing !== 'editing'}
            />
          </Box>
          <Box>
            <Typography variant="h5" textAlign="center">
              Users
            </Typography>
            <UserList
              users={editing === 'editing' ? users : assignmentUsers}
              groups={groups}
              checkbox={
                editing === 'editing'
                  ? {
                      onClick: setCheckedUsers,
                      checked: checkedUsers,
                    }
                  : undefined
              }
              stats
              userCourseData={userCourseData}
              onApproveUser={(val: string) => {
                setCheckedUsers([val])
                setCheckedCourses([])
                setEditing('approving')
              }}
            />
          </Box>
        </Box>
        {editing === 'approving' && (
          <Box ref={ref}>
            <Divider
              sx={{
                borderColor: theme.palette.primary.dark,
                opacity: 0.75,
                my: 1,
              }}
            />
            <Box sx={{ my: 2 }}>
              <Typography variant="h5" textAlign="center">
                Give Course Credit
              </Typography>
              <Typography
                color="text.secondary"
                variant="subtitle2"
                sx={{ mt: 1, mb: 0.5 }}
              >
                You may manually give credit to users for completing courses
                within this assignment. Select a user or users and the course(s)
                to mark as having been completed. Doing so will count as
                progress toward completing the assignment.
              </Typography>
              {assignmentsCompletedWithinThreshold.length > 0 && (
                <Typography color="text.secondary" variant="subtitle2">
                  This assignment has{' '}
                  {assignmentsCompletedWithinThreshold.length} instance
                  {assignmentsCompletedWithinThreshold.length === 1
                    ? ''
                    : 's'}{' '}
                  of courses that have already been completed within the time
                  threshold set by your linked service.{' '}
                  <Link
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setDialogOpen(true)}
                  >
                    Click here
                  </Link>{' '}
                  to review these cases and to possibly give those users credit
                  for their recently completed courses.
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                columnGap: 2,
              }}
            >
              <Box>
                <Typography variant="h5" textAlign="center">
                  Users
                </Typography>
                <UserList
                  users={assignmentUsers}
                  groups={groups}
                  checkbox={
                    editing
                      ? {
                          onClick: setCheckedUsers,
                          checked: checkedUsers,
                        }
                      : undefined
                  }
                  userCourseData={userCourseData}
                />
              </Box>
              <Box>
                <Typography variant="h5" textAlign="center">
                  Courses
                </Typography>
                <CourseList
                  courses={hydratedCourses}
                  trainingSeries={trainingSeries}
                  checkbox={{
                    onClick: setCheckedCourses,
                    checked: checkedCourses,
                  }}
                  stats={!editing}
                />
              </Box>
            </Box>
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          {editing ? (
            <>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<SubmitIcon />}
                disabled={!assignmentName}
                onClick={
                  editing === 'editing'
                    ? () => {
                        setSaving(true)
                        updateAssignment(
                          assignmentId!,
                          activeLinkedService,
                          assignmentName,
                          dueDate,
                          checkedUsers,
                          checkedCourses,
                        )
                          .then(() => dispatch(fetchAssignments()))
                          .then(() =>
                            dispatch(
                              fetchCompletedAssignmentsWithinThreshold(
                                assignmentId!,
                              ),
                            ),
                          )
                          .then(() => dispatch(fetchCompletedCourses()))
                          .then(() => setEditing(''))
                          .then(() => setSaving(false))
                          .catch((err) => {
                            setErrorMessage(err?.response?.data)
                            setSaving(false)
                          })
                      }
                    : () => {
                        setSaving(true)
                        axios
                          .post('/api/manage/assignment-courses/complete', {
                            assignmentID: assignmentId,
                            userIDs: checkedUsers,
                            courseIDs: checkedCourses,
                          })
                          .then(() => dispatch(fetchAssignments()))
                          .then(() =>
                            dispatch(
                              fetchCompletedAssignmentsWithinThreshold(
                                assignmentId!,
                              ),
                            ),
                          )
                          .then(() => dispatch(fetchCompletedCourses()))
                          .then(() => dispatch(fetchCompletedAssignments()))
                          .then(() => setEditing(''))
                          .then(() => setSaving(false))
                          .catch((err) => {
                            setErrorMessage(err?.response?.data)
                            setSaving(false)
                          })
                      }
                }
              >
                Update
              </Button>
              <Snackbar
                open={!!errorMessage}
                onClose={() => setErrorMessage('')}
              >
                <Alert
                  variant="filled"
                  onClose={() => setErrorMessage('')}
                  severity="error"
                >
                  <strong>Error Updating Assignment: </strong>
                  {errorMessage}
                </Alert>
              </Snackbar>
              <Button
                variant="contained"
                size="large"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => {
                  setAssignmentName(assignment?.name || '')
                  setCheckedUsers(assignment?.users || [])
                  setCheckedCourses(assignment?.courses || [])
                  setDueDate(assignment?.dueDate)
                  setEditing('')
                }}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button
                sx={{ flexBasis: 207 }}
                variant="contained"
                size="large"
                startIcon={<EditIcon />}
                onClick={() => {
                  let current = editing
                  setEditing('editing')
                  if (current !== 'editing') {
                    setCheckedUsers(assignment?.users || [])
                  }
                  if (current !== 'editing') {
                    setCheckedCourses(assignment?.courses || [])
                  }
                }}
              >
                Edit Assignment
              </Button>
              <Button
                sx={{ flexBasis: 207 }}
                variant="outlined"
                size="large"
                startIcon={<GiveCreditIcon />}
                onClick={() => {
                  setEditing('approving')
                  setCheckedUsers([])
                  setCheckedCourses([])
                }}
              >
                Credit Courses
              </Button>
            </>
          )}
        </Box>
      </Box>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogContent>
          <Typography variant="h6" textAlign="center">
            Credit Completed Users
          </Typography>
          <Typography sx={{ px: 0.25, mt: 1 }}>
            This is a list of completed courses by users in this assignment
            within the time threshold set by your linked service:
          </Typography>
          <Box
            sx={{
              my: 2,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              maxHeight: 240,
              overflow: 'scroll',
              border: `1px solid ${theme.palette.primary.dark}`,
            }}
          >
            <Box
              sx={{
                backgroundColor: theme.palette.primary.dark,
                p: 1,
                py: 0.5,
              }}
            >
              User
            </Box>
            <Box
              sx={{
                backgroundColor: theme.palette.primary.dark,
                p: 1,
                py: 0.5,
              }}
            >
              Course
            </Box>
            {assignmentsCompletedWithinThreshold?.map((ca, i) => (
              <React.Fragment key={ca.userID + ca.courseID}>
                <Box
                  sx={{
                    backgroundColor: i % 2 ? theme.palette.divider : undefined,
                    px: 1,
                    py: 0.5,
                  }}
                >
                  {users.find((u) => u.id === ca.userID)?.name}
                </Box>
                <Box
                  sx={{
                    backgroundColor: i % 2 ? theme.palette.divider : undefined,
                    px: 1,
                    py: 0.5,
                  }}
                >
                  {courses.find((c) => c.id === ca.courseID)?.name}
                </Box>
              </React.Fragment>
            ))}
          </Box>
          <Typography sx={{ px: 0.25, mb: 0.5 }}>
            Would you like to mark these courses as complete for this
            assignment?
          </Typography>
          <Typography
            color="text.secondary"
            variant="subtitle2"
            sx={{ px: 0.25 }}
          >
            *If you select YES, users will not have to retake recently completed
            courses. All courses listed above will be marked as complete for
            their respective users for this assignment.
          </Typography>
          <Typography
            color="text.secondary"
            variant="subtitle2"
            sx={{ px: 0.25 }}
          >
            *If you select NO, users will have to take every course in the
            assignment, even those that they have recently completed. You can
            still give credit for individual courses for users or change this
            selection later if desired.
          </Typography>
          <Box
            sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}
          >
            <Button
              variant="contained"
              onClick={() => {
                setDialogOpen(false)
                setSaving(true)
                axios
                  .post(
                    '/api/manage/assignment-courses/complete/within-threshold',
                    {
                      assignmentID: assignmentId,
                    },
                  )
                  .then(() => dispatch(fetchAssignments))
                  .then(() =>
                    dispatch(
                      fetchCompletedAssignmentsWithinThreshold(assignmentId!),
                    ),
                  )
                  .then(() => dispatch(fetchCompletedCourses()))
                  .then(() => dispatch(fetchCompletedAssignments()))
                  .then(() => setEditing(''))
                  .then(() => setSaving(false))
                  .catch((err) => {
                    setErrorMessage(
                      err?.response?.data || 'Bulk Completion Error',
                    )
                    setSaving(false)
                  })
              }}
            >
              Yes
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => setDialogOpen(false)}
            >
              No
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <Loading
        entities={[
          assignmentsCompletedWithinThresholdStatus,
          assignmentsStatus,
          completedCoursesStatus,
          coursesStatus,
          groupsStatus,
          trainingSeriesStatus,
          usersStatus,
        ]}
        loading={saving}
      />
    </>
  )
}
